/**
 * Font catalog — Latin-subset families vendored to `cdn.{env}.fias.io`.
 *
 * The catalog is the **source of truth** for what gets vendored. The
 * sync script (`scripts/sync-platform-resources.ts`) reads this file,
 * fetches `.woff2` bytes from `fonts.gstatic.com`, writes content-
 * addressed files to `vendor/fonts/`, and emits `manifest.json`. Runtime
 * helpers in `client.ts` then read the manifest at module load.
 *
 * Family list is the **strict union** of three call sites today:
 *  - `client/index.html` main-app families (5)
 *  - `packages/arche-sdk/src/themes/fonts.ts` palette families (45)
 *  - `client/src/arche/arches/image-editor/lib/fonts.ts` picker (~60)
 *
 * **Weights are explicit static cuts**, not variable-axis ranges. Google's
 * CSS2 API rejects variable ranges that fall outside a font's actual axis
 * (Open Sans is 300..800, not 100..900) so per-family range bookkeeping
 * would be required. Enumerating discrete cuts trades a small per-cut
 * download for robust, reproducible requests. Variable-axis fonts whose
 * cuts collapse onto the same `.woff2` upstream get content-addressed
 * dedup in the manifest, so storage cost is recovered.
 *
 * The `variable` boolean stays as a manifest hint (consumers can prefer
 * variable-axis behavior when CSS supports it via @font-face weight
 * ranges) but does NOT affect the request URL.
 *
 * Bumping `CATALOG_VERSION` writes to a fresh CDN path prefix — old
 * URLs remain immutable, no invalidation ever needed.
 */

import type { FontLicense } from './manifest-schema';

/** Picker chip categories (image-editor reuses this for filtering). */
export type FontCategory = 'sans' | 'serif' | 'display' | 'mono' | 'handwriting' | 'system';

/** A discrete weight cut. */
export type CatalogWeight = number;

export type CatalogStyle = 'regular' | 'italic';

/**
 * A vendored font family. `system: true` entries are OS fonts (Arial,
 * Helvetica, …) carried in the catalog only so the image-editor picker
 * can offer them — they are never vendored to the CDN.
 */
export interface CatalogFamily {
  /** Stable kebab-case id; also the CDN path segment. */
  id: string;
  /** CSS `font-family` name. */
  family: string;
  /** Picker label (often identical to `family`). */
  displayName: string;
  category: FontCategory;
  /** OS-resident family; do not vendor. */
  system?: true;
  /** Manifest hint — `true` if Google publishes a variable-axis file
   *  for this family. Does NOT affect the request URL; the sync script
   *  enumerates discrete cuts in all cases. */
  variable: boolean;
  /** Weights to vendor. */
  weights: readonly CatalogWeight[];
  /** Styles to vendor for each weight. */
  styles: readonly CatalogStyle[];
  /** License — REQUIRED. The sync script refuses to run without it. */
  license: FontLicense | null;
}

/** Bump this when fonts or generated CSS change. New value = fresh CDN
 *  prefix = no cache invalidation ever needed.
 *
 *  v1 — initial catalog (2026-05). */
export const CATALOG_VERSION = 'v1';

// --- Catalog construction helpers ----------------------------------------

/**
 * A vendored Google family with the default modern set of cuts
 * (400/500/600/700, both styles). Suitable for sans-serif body/UI fonts.
 */
function modernSans(
  id: string,
  family: string,
  license: FontLicense,
  opts: { variable?: boolean } = {},
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'sans',
    variable: opts.variable ?? true,
    weights: [400, 500, 600, 700],
    styles: ['regular', 'italic'],
    license,
  };
}

/** Sans-serif with a custom weight list. */
function sansWeights(
  id: string,
  family: string,
  license: FontLicense,
  weights: readonly number[],
  opts: { variable?: boolean; italic?: boolean } = {},
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'sans',
    variable: opts.variable ?? true,
    weights,
    styles: opts.italic === false ? ['regular'] : ['regular', 'italic'],
    license,
  };
}

/** Serif with the modern body-font cut set. */
function modernSerif(
  id: string,
  family: string,
  license: FontLicense,
  opts: { variable?: boolean } = {},
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'serif',
    variable: opts.variable ?? true,
    weights: [400, 500, 600, 700],
    styles: ['regular', 'italic'],
    license,
  };
}

/** Serif with a custom weight list. */
function serifWeights(
  id: string,
  family: string,
  license: FontLicense,
  weights: readonly number[],
  opts: { variable?: boolean; italic?: boolean } = {},
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'serif',
    variable: opts.variable ?? false,
    weights,
    styles: opts.italic === false ? ['regular'] : ['regular', 'italic'],
    license,
  };
}

/** Mono with explicit cuts. */
function monoWeights(
  id: string,
  family: string,
  license: FontLicense,
  weights: readonly number[],
  opts: { variable?: boolean; italic?: boolean } = {},
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'mono',
    variable: opts.variable ?? true,
    weights,
    styles: opts.italic === false ? ['regular'] : ['regular', 'italic'],
    license,
  };
}

/** Display font — typically single-weight, no italic. */
function displayFont(
  id: string,
  family: string,
  license: FontLicense,
  weights: readonly number[] = [400],
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'display',
    variable: false,
    weights,
    styles: ['regular'],
    license,
  };
}

/** Handwriting font — typically single-weight, no italic. */
function handwritingFont(
  id: string,
  family: string,
  license: FontLicense,
  weights: readonly number[] = [400],
): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category: 'handwriting',
    variable: false,
    weights,
    styles: ['regular'],
    license,
  };
}

/**
 * An OS-resident font (Arial, Helvetica, …). Carried for picker UX only
 * — never vendored, never has a license entry.
 */
function systemFont(id: string, family: string, category: FontCategory): CatalogFamily {
  return {
    id,
    family,
    displayName: family,
    category,
    system: true,
    variable: false,
    weights: [],
    styles: [],
    license: null,
  };
}

// --- The catalog ---------------------------------------------------------

/**
 * Order: system fonts first, then alphabetical within each Google
 * category. Per-family weight specs match the union of what existing
 * consumers request today (arche-SDK theme palettes + image-editor
 * picker + client/index.html main-app fonts).
 */
export const FONT_CATALOG: readonly CatalogFamily[] = [
  // System fonts (image-editor picker only; never vendored).
  systemFont('arial', 'Arial', 'sans'),
  systemFont('helvetica', 'Helvetica', 'sans'),
  systemFont('georgia', 'Georgia', 'serif'),
  systemFont('times-new-roman', 'Times New Roman', 'serif'),
  systemFont('courier-new', 'Courier New', 'mono'),
  systemFont('verdana', 'Verdana', 'sans'),

  // Sans-serif (variable-axis on most; ~25 families)
  modernSans('inter', 'Inter', 'OFL'),
  modernSans('roboto', 'Roboto', 'Apache-2.0'),
  sansWeights('open-sans', 'Open Sans', 'OFL', [400, 500, 600, 700]),
  sansWeights('lato', 'Lato', 'OFL', [300, 400, 700], { variable: false }),
  sansWeights('montserrat', 'Montserrat', 'OFL', [400, 500, 600, 700, 800]),
  modernSans('poppins', 'Poppins', 'OFL', { variable: false }),
  sansWeights('nunito', 'Nunito', 'OFL', [400, 600, 700]),
  modernSans('nunito-sans', 'Nunito Sans', 'OFL'),
  sansWeights('raleway', 'Raleway', 'OFL', [400, 500, 600]),
  sansWeights('oswald', 'Oswald', 'OFL', [400, 700], { italic: false }),
  sansWeights('source-sans-3', 'Source Sans 3', 'OFL', [400, 500, 600]),
  sansWeights('work-sans', 'Work Sans', 'OFL', [400, 500, 600]),
  modernSans('mulish', 'Mulish', 'OFL'),
  modernSans('dm-sans', 'DM Sans', 'OFL'),
  modernSans('manrope', 'Manrope', 'OFL'),
  modernSans('outfit', 'Outfit', 'OFL'),
  modernSans('plus-jakarta-sans', 'Plus Jakarta Sans', 'OFL'),
  modernSans('space-grotesk', 'Space Grotesk', 'OFL'),
  sansWeights('karla', 'Karla', 'OFL', [400, 500, 600]),
  modernSans('quicksand', 'Quicksand', 'OFL'),
  modernSans('barlow', 'Barlow', 'OFL'),
  modernSans('rubik', 'Rubik', 'OFL'),
  modernSans('lexend', 'Lexend', 'OFL'),
  modernSans('sora', 'Sora', 'OFL'),
  sansWeights('cabin', 'Cabin', 'OFL', [400, 500, 600]),
  modernSans('assistant', 'Assistant', 'OFL'),
  sansWeights('atkinson-hyperlegible', 'Atkinson Hyperlegible', 'OFL', [400, 700]),

  // Serif
  modernSerif('playfair-display', 'Playfair Display', 'OFL'),
  serifWeights('merriweather', 'Merriweather', 'OFL', [400, 700]),
  modernSerif('lora', 'Lora', 'OFL'),
  serifWeights('pt-serif', 'PT Serif', 'OFL', [400, 700]),
  modernSerif('source-serif-4', 'Source Serif 4', 'OFL'),
  modernSerif('eb-garamond', 'EB Garamond', 'OFL'),
  serifWeights('cormorant-garamond', 'Cormorant Garamond', 'OFL', [400, 500, 600]),
  serifWeights('crimson-pro', 'Crimson Pro', 'OFL', [400, 600, 700]),
  modernSerif('spectral', 'Spectral', 'OFL'),
  serifWeights('libre-baskerville', 'Libre Baskerville', 'OFL', [400, 700]),
  modernSerif('bitter', 'Bitter', 'OFL'),
  serifWeights('cardo', 'Cardo', 'OFL', [400, 700]),
  serifWeights('cinzel', 'Cinzel', 'OFL', [400, 600, 700], { italic: false }),

  // Display (single-weight typically; no italic in Google's catalog for most)
  displayFont('bebas-neue', 'Bebas Neue', 'OFL'),
  displayFont('anton', 'Anton', 'OFL'),
  displayFont('archivo-black', 'Archivo Black', 'OFL'),
  displayFont('lobster', 'Lobster', 'OFL'),
  displayFont('pacifico', 'Pacifico', 'OFL'),
  displayFont('permanent-marker', 'Permanent Marker', 'OFL'),
  displayFont('righteous', 'Righteous', 'OFL'),
  {
    id: 'comfortaa',
    family: 'Comfortaa',
    displayName: 'Comfortaa',
    category: 'display',
    variable: true,
    weights: [400, 500, 600, 700],
    styles: ['regular'],
    license: 'OFL',
  },
  {
    id: 'fredoka',
    family: 'Fredoka',
    displayName: 'Fredoka',
    category: 'display',
    variable: true,
    weights: [400, 500, 600, 700],
    styles: ['regular'],
    license: 'OFL',
  },
  displayFont('abril-fatface', 'Abril Fatface', 'OFL'),
  displayFont('patua-one', 'Patua One', 'OFL'),
  displayFont('russo-one', 'Russo One', 'OFL'),

  // Chunky / playful display faces — kids'-book variety the catalog lacked
  // (the existing rounded faces are medium-weight; these are the heavy
  // "bubble" look, plus comic, slab, and a themed display).
  {
    id: 'baloo-2',
    family: 'Baloo 2',
    displayName: 'Baloo 2',
    category: 'display',
    variable: true,
    weights: [400, 500, 600, 700, 800],
    styles: ['regular'],
    license: 'OFL',
  },
  displayFont('lilita-one', 'Lilita One', 'OFL'),
  displayFont('titan-one', 'Titan One', 'OFL'),
  displayFont('bubblegum-sans', 'Bubblegum Sans', 'OFL'),
  displayFont('chewy', 'Chewy', 'Apache-2.0'),
  displayFont('concert-one', 'Concert One', 'OFL'),
  displayFont('bangers', 'Bangers', 'OFL'),
  displayFont('luckiest-guy', 'Luckiest Guy', 'Apache-2.0'),
  displayFont('alfa-slab-one', 'Alfa Slab One', 'OFL'),
  displayFont('creepster', 'Creepster', 'OFL'),

  // Mono
  monoWeights('roboto-mono', 'Roboto Mono', 'Apache-2.0', [400, 500]),
  monoWeights('source-code-pro', 'Source Code Pro', 'OFL', [400, 500]),
  monoWeights('jetbrains-mono', 'JetBrains Mono', 'OFL', [400, 500]),
  monoWeights('fira-code', 'Fira Code', 'OFL', [400, 500], { italic: false }),
  monoWeights('space-mono', 'Space Mono', 'OFL', [400, 700], { variable: false }),
  monoWeights('ibm-plex-mono', 'IBM Plex Mono', 'OFL', [400, 500], { variable: false }),
  monoWeights('inconsolata', 'Inconsolata', 'OFL', [400, 500], { italic: false }),
  monoWeights('ubuntu-mono', 'Ubuntu Mono', 'OFL', [400, 700], { variable: false }),
  monoWeights('dm-mono', 'DM Mono', 'OFL', [400, 500], { variable: false }),
  monoWeights('fragment-mono', 'Fragment Mono', 'OFL', [400], { variable: false }),

  // Handwriting
  {
    id: 'dancing-script',
    family: 'Dancing Script',
    displayName: 'Dancing Script',
    category: 'handwriting',
    variable: true,
    weights: [400, 500, 600, 700],
    styles: ['regular'],
    license: 'OFL',
  },
  {
    id: 'caveat',
    family: 'Caveat',
    displayName: 'Caveat',
    category: 'handwriting',
    variable: true,
    weights: [400, 500, 600, 700],
    styles: ['regular'],
    license: 'OFL',
  },
  handwritingFont('shadows-into-light', 'Shadows Into Light', 'OFL'),
  handwritingFont('indie-flower', 'Indie Flower', 'OFL'),
  handwritingFont('sacramento', 'Sacramento', 'OFL'),
  handwritingFont('satisfy', 'Satisfy', 'OFL'),
  handwritingFont('great-vibes', 'Great Vibes', 'OFL'),
  handwritingFont('kalam', 'Kalam', 'OFL', [300, 400, 700]),
  // Neat kid-style print hand (distinct from the existing cursive scripts).
  handwritingFont('patrick-hand', 'Patrick Hand', 'OFL'),
];

/** Convenience: families that actually get vendored (drops system fonts). */
export const VENDORED_FAMILIES: readonly CatalogFamily[] = FONT_CATALOG.filter((f) => !f.system);

/** Lookup by family name (CSS `font-family` string). Used by the SDK
 *  theme migration and the image-editor picker. */
export function findFamily(family: string): CatalogFamily | undefined {
  return FONT_CATALOG.find((f) => f.family === family);
}
